/**
 * Login — reproduction 1:1 de src/pages/Login.tsx du repo Loveable.
 * Différences :
 *   - Soumission via api.login (vraie API JWT) au lieu du mock localStorage.
 *   - Redirection post-login selon le rôle (admin/super_admin → /admin,
 *     technicien → /technicien, client → /dashboard).
 */
import { ArrowLeft, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { extractErrorMessage } from '@/features/auth/error-message';
import { login as apiLogin } from '@/features/auth/auth-api';
import { useAuth } from '@/lib/auth-context';

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { setSession } = useAuth();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    setIsLoading(true);
    try {
      const res = await apiLogin({ email, password });
      setSession(res.token, res.user);
      toast.success('Connexion réussie');
      const target =
        res.user.role === 'admin' || res.user.role === 'super_admin'
          ? '/admin'
          : res.user.role === 'technicien'
            ? '/technicien'
            : '/dashboard';
      navigate(target);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-bcl-grey">
      <div className="w-full max-w-md">
        <div className="text-sm mb-8">
          <Button variant="ghost" size="sm" asChild>
            <Link
              to="/"
              className="flex items-center text-gray-500 hover:text-bcl-blue"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Retour à l&apos;accueil
            </Link>
          </Button>
        </div>
        <div className="bg-white rounded-xl shadow-lg px-8 py-10 animate-scale-in">
          <div className="mb-8 text-center">
            <h1 className="text-2xl font-bold mb-2">Connexion</h1>
            <p className="text-gray-600">
              Accédez à votre espace client BCL Telecom
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email">Adresse e-mail</Label>
              <Input
                id="email"
                type="email"
                placeholder="nom@exemple.fr"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                disabled={isLoading}
                required
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Mot de passe</Label>
                <Link
                  to="/forgot-password"
                  className="text-sm text-bcl-blue hover:underline"
                >
                  Mot de passe oublié ?
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                disabled={isLoading}
                required
              />
            </div>

            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Connexion en cours...
                </>
              ) : (
                'Se connecter'
              )}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm">
            <span className="text-gray-600">
              Vous n&apos;avez pas de compte ?
            </span>{' '}
            <Link
              to="/signup"
              className="text-bcl-blue hover:underline font-medium"
            >
              S&apos;inscrire
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
